import { useCallback, useId, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  ArrowRight,
  CalendarRange,
  ChartPie,
  Check,
  CircleHelp,
  CircleDollarSign,
  FileJson,
  Folders,
  MonitorSmartphone,
  Rss,
  ShieldCheck,
  Sparkles,
  Trophy,
  UserRound,
  X,
} from 'lucide-react';
import tw, { styled } from 'twin.macro';

import PortfolioData from '../../components/PortfolioData';
import ImportStringDialog from '../../components/ImportStringDialog';
import TechnicalDetails from '../../components/TechnicalDetails';
import { useMigrationHandoff } from '../../hooks/useMigrationHandoff';
import Logo from '../../images/logo.svg';
import { copyImportString, getImportString } from '../../utils/copyImportString';

const Page = styled.div`
  ${tw`min-h-screen overflow-hidden bg-white text-zinc-950`}

  main {
    animation: page-settle 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @keyframes page-settle {
    from {
      opacity: 0;
      transform: translateY(0.5rem);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    main {
      animation: none;
    }
  }
`;

const comparisonRows = [
  {
    label: 'Använd utan konto',
    description: 'Du kan komma igång och spara portföljen utan att skapa en användare.',
    icon: UserRound,
    main: true,
    legacy: true,
  },
  {
    label: 'Aktieutdelningar & rapporter',
    description: 'Visar utdelningsdata för aktier och kommande bolagsrapporter.',
    icon: CircleDollarSign,
    main: true,
    legacy: true,
  },
  {
    label: 'Topplista & bloggflöden',
    description: 'Visar aktiernas topplista och samlar aktuella inlägg från ekonomibloggar.',
    icon: Rss,
    main: true,
    legacy: true,
  },
  {
    label: 'Konto & flera enheter',
    description: 'Med konto synkas portföljen så att du kan se den på flera enheter.',
    icon: MonitorSmartphone,
    main: true,
    legacy: false,
  },
  {
    label: 'Flera portföljer',
    description: 'Stöd för flera separata portföljer är planerat och kommer längre fram.',
    icon: Folders,
    main: true,
    legacy: false,
    isSoon: true,
  },
  {
    label: 'Transaktionsportfölj',
    description:
      'Registrera köp- och säljdatum för att följa utdelningar korrekt över tid. Funktionen kommer inte till legacy-appen, men lanseras snart i huvudappen.',
    icon: CalendarRange,
    main: true,
    legacy: false,
    isSoon: true,
  },
  {
    label: 'ETF-utdelningar',
    description: 'Visar utdelningar för börshandlade fonder.',
    icon: ChartPie,
    main: true,
    legacy: false,
    isNew: true,
  },
  {
    label: 'Prestationer',
    description: 'Milstolpar som följer din portfölj och utdelningsresa.',
    icon: Trophy,
    main: true,
    legacy: false,
  },
  {
    label: 'All ny framtida funktionalitet',
    description: 'Framtida funktioner och större förbättringar prioriteras i huvudappen.',
    icon: Sparkles,
    main: true,
    legacy: false,
  },
];

function FeatureTooltip({ description, label }) {
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span tw="relative ml-auto inline-flex shrink-0">
      <button
        type="button"
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-label={`Förklaring av ${label}`}
        onBlur={() => setIsOpen(false)}
        onFocus={() => setIsOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setIsOpen(false);
          }
        }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        tw="flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-700 focus:outline-none focus-visible:bg-zinc-100 focus-visible:text-zinc-700 focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <CircleHelp size={16} aria-hidden="true" />
      </button>
      {isOpen && (
        <span
          id={tooltipId}
          role="tooltip"
          tw="pointer-events-none absolute bottom-[calc(100%+0.45rem)] right-0 z-20 w-52 rounded-md bg-zinc-950 px-3 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg sm:left-1/2 sm:right-auto sm:-translate-x-1/2"
        >
          {description}
          <span tw="absolute right-3 top-full h-2 w-2 -translate-y-1/2 rotate-45 bg-zinc-950 sm:left-1/2 sm:right-auto sm:-translate-x-1/2" />
        </span>
      )}
    </span>
  );
}

function FeatureLabel({ row }) {
  const Icon = row.icon;

  return (
    <span tw="flex w-full min-w-0 items-center gap-2">
      <span tw="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
        <Icon size={15} strokeWidth={2} aria-hidden="true" />
      </span>
      <span tw="flex min-w-0 flex-wrap items-center gap-2.5">
        {row.label}
        {row.isNew && (
          <span tw="rounded bg-indigo-100 px-1.5 py-0.5 text-[0.6rem] font-bold text-indigo-700 sm:text-[0.65rem]">
            NYHET
          </span>
        )}
        {row.isSoon && (
          <span tw="rounded bg-amber-100 px-1.5 py-0.5 text-[0.6rem] font-bold text-amber-800 sm:text-[0.65rem]">
            KOMMER SNART
          </span>
        )}
      </span>
      <FeatureTooltip description={row.description} label={row.label} />
    </span>
  );
}

function Availability({ status }) {
  return (
    <span tw="inline-flex items-center justify-center">
      {status ? (
        <span tw="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-200 text-emerald-900 ring-1 ring-inset ring-emerald-300">
          <Check size={18} strokeWidth={2.5} aria-hidden="true" />
          <span tw="sr-only">Ingår</span>
        </span>
      ) : (
        <span tw="flex h-8 w-8 items-center justify-center rounded-full bg-red-200 text-red-900">
          <X size={18} strokeWidth={2} aria-hidden="true" />
          <span tw="sr-only">Ingår inte</span>
        </span>
      )}
    </span>
  );
}

function DestinationButton({ children, isLoading, mobileLabel, onClick, isPrimary }) {
  return (
    <button
      type="button"
      aria-label={children}
      onClick={onClick}
      disabled={isLoading}
      css={[
        tw`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg px-1 py-2.5 text-center text-[0.7rem] font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 motion-reduce:transition-none sm:px-4 sm:text-sm`,
        isPrimary
          ? tw`bg-indigo-600 text-white hover:bg-indigo-700`
          : tw`border border-zinc-300 bg-white text-zinc-950 hover:bg-zinc-50`,
      ]}
    >
      <span tw="whitespace-nowrap sm:hidden">{mobileLabel}</span>
      <span tw="hidden sm:inline">{children}</span>
      {!isLoading && <ArrowRight size={16} aria-hidden="true" tw="hidden shrink-0 sm:block" />}
    </button>
  );
}

function ImportStringButton({ onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      tw="mt-3 inline-flex min-h-10 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-semibold text-indigo-600 underline decoration-indigo-200 underline-offset-4 transition hover:text-indigo-700 hover:decoration-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:text-sm"
    >
      <FileJson size={15} aria-hidden="true" />
      Visa importsträng
    </button>
  );
}

function DesktopComparison({
  hasPortfolio,
  migrateLegacy,
  migrateMain,
  migratingTarget,
  onOpenImportString,
}) {
  const isLoading = Boolean(migratingTarget);

  return (
    <div tw="relative mt-3 hidden sm:block">
      <span tw="absolute left-[56.5%] top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-md bg-indigo-600 px-2.5 py-1 text-[0.65rem] font-semibold text-white shadow-sm sm:text-xs">
        <span tw="sm:hidden">Rek.</span>
        <span tw="hidden sm:inline">Rekommenderas</span>
      </span>
      <div tw="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_12px_34px_rgba(24,24,27,0.07)]">
        <table tw="w-full table-fixed border-collapse text-left">
          <colgroup>
            <col tw="w-[42%]" />
            <col tw="w-[29%]" />
            <col tw="w-[29%]" />
          </colgroup>
          <thead>
            <tr>
              <th scope="col" tw="px-3 py-4 align-bottom sm:px-6 sm:py-5">
                <span tw="text-sm font-semibold text-zinc-600">Jämför funktioner</span>
              </th>
              <th
                scope="col"
                tw="border-x border-indigo-100 bg-indigo-50/70 px-2 pb-4 pt-7 text-center align-bottom sm:px-5 sm:pb-5 sm:pt-7"
              >
                <span tw="sr-only">Rekommenderas. </span>
                <strong tw="block text-sm font-bold text-zinc-950 sm:text-base">Nya appen</strong>
                <span tw="mt-1 block text-sm font-semibold text-indigo-700">Gratis</span>
              </th>
              <th scope="col" tw="px-2 py-4 text-center align-bottom sm:px-5 sm:py-5">
                <strong tw="block text-sm font-bold text-zinc-950 sm:text-base">Gamla appen</strong>
                <span tw="mt-1 block text-sm font-semibold text-indigo-700">Gratis</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map((row) => (
              <tr key={row.label} tw="border-t border-zinc-200">
                <th
                  scope="row"
                  tw="px-3 py-3 text-xs font-medium leading-5 text-zinc-700 sm:px-6 sm:py-3.5 sm:text-sm"
                >
                  <FeatureLabel row={row} />
                </th>
                <td tw="border-x border-indigo-100 bg-indigo-50/40 px-2 py-3 text-center sm:py-3.5">
                  <Availability status={row.main} />
                </td>
                <td tw="px-2 py-3 text-center sm:py-3.5">
                  <Availability status={row.legacy} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr tw="border-t border-zinc-200">
              <th scope="row" tw="px-3 py-4 align-middle sm:px-6 sm:py-5">
                <span tw="text-xs font-normal leading-5 text-zinc-500 sm:text-sm">
                  Kopian ligger kvar här efter att du har flyttat innehavet.
                </span>
              </th>
              <td tw="border-x border-indigo-100 bg-indigo-50/40 p-2 align-middle sm:p-4">
                <DestinationButton
                  isLoading={isLoading}
                  mobileLabel={migratingTarget === 'main' ? 'Förbereder…' : 'Till nya appen'}
                  onClick={migrateMain}
                  isPrimary
                >
                  {migratingTarget === 'main'
                    ? 'Förbereder…'
                    : hasPortfolio
                      ? 'Flytta till nya appen'
                      : 'Öppna nya appen'}
                </DestinationButton>
              </td>
              <td tw="p-2 align-middle sm:p-4">
                <DestinationButton
                  isLoading={isLoading}
                  mobileLabel={
                    migratingTarget === 'legacy' ? 'Förbereder…' : 'Fortsätt i gamla appen'
                  }
                  onClick={migrateLegacy}
                >
                  {migratingTarget === 'legacy' ? 'Förbereder…' : 'Fortsätt i gamla appen'}
                </DestinationButton>
              </td>
            </tr>
            <tr tw="border-t border-zinc-200">
              <td colSpan="3" tw="px-3 pb-3 text-center sm:px-6 sm:pb-4">
                <ImportStringButton onOpen={onOpenImportString} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function MobileComparison({
  hasPortfolio,
  migrateLegacy,
  migrateMain,
  migratingTarget,
  onOpenImportString,
}) {
  const isLoading = Boolean(migratingTarget);

  return (
    <div tw="relative mt-3 sm:hidden">
      <span tw="absolute left-1/4 top-0 z-10 -translate-x-1/2 -translate-y-1/2 rounded-md bg-indigo-600 px-2.5 py-1 text-[0.65rem] font-semibold text-white shadow-sm">
        Rek.
      </span>
      <div tw="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_12px_34px_rgba(24,24,27,0.07)]">
        <div tw="grid grid-cols-2">
          <div tw="border-r border-indigo-100 bg-indigo-50/70 px-3 pb-4 pt-7 text-center">
            <strong tw="block text-sm font-bold text-zinc-950">Nya appen</strong>
            <span tw="mt-1 block text-sm font-semibold text-indigo-700">Gratis</span>
          </div>
          <div tw="px-3 pb-4 pt-7 text-center">
            <strong tw="block text-sm font-bold text-zinc-950">Gamla appen</strong>
            <span tw="mt-1 block text-sm font-semibold text-indigo-700">Gratis</span>
          </div>
        </div>

        <div>
          {comparisonRows.map((row) => (
            <div key={row.label} tw="border-t border-zinc-200 px-3 py-3">
              <div tw="text-sm font-medium leading-5 text-zinc-700">
                <FeatureLabel row={row} />
              </div>
              <div tw="mt-2 grid grid-cols-2 overflow-hidden rounded-lg border border-zinc-200">
                <div tw="flex items-center justify-between border-r border-indigo-100 bg-indigo-50/50 px-3 py-2">
                  <span tw="text-xs font-medium text-zinc-500">Nya appen</span>
                  <Availability status={row.main} />
                </div>
                <div tw="flex items-center justify-between px-3 py-2">
                  <span tw="text-xs font-medium text-zinc-500">Gamla appen</span>
                  <Availability status={row.legacy} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div tw="border-t border-zinc-200 px-3 py-3">
          <p tw="text-xs leading-5 text-zinc-500">
            Kopian ligger kvar här efter att du har flyttat innehavet.
          </p>
          <div tw="mt-3 grid grid-cols-2 gap-2">
            <DestinationButton
              isLoading={isLoading}
              mobileLabel={migratingTarget === 'main' ? 'Förbereder…' : 'Till nya appen'}
              onClick={migrateMain}
              isPrimary
            >
              {migratingTarget === 'main'
                ? 'Förbereder…'
                : hasPortfolio
                  ? 'Flytta till nya appen'
                  : 'Öppna nya appen'}
            </DestinationButton>
            <DestinationButton
              isLoading={isLoading}
              mobileLabel={migratingTarget === 'legacy' ? 'Förbereder…' : 'Fortsätt i gamla appen'}
              onClick={migrateLegacy}
            >
              {migratingTarget === 'legacy' ? 'Förbereder…' : 'Fortsätt i gamla appen'}
            </DestinationButton>
          </div>
          <div tw="-mx-3 mt-3 border-t border-zinc-200 px-3 pt-0 text-center">
            <ImportStringButton onOpen={onOpenImportString} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Comparison(props) {
  return (
    <>
      <MobileComparison {...props} />
      <DesktopComparison {...props} />
    </>
  );
}

export default function Retirement() {
  const { error, hasPortfolio, migrateLegacy, migrateMain, migratingTarget } =
    useMigrationHandoff();
  const [isTechnicalOpen, setIsTechnicalOpen] = useState(false);
  const [isImportStringOpen, setIsImportStringOpen] = useState(false);
  const [manualImportString, setManualImportString] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const technicalTriggerRef = useRef(null);
  const importStringTriggerRef = useRef(null);

  const openImportString = useCallback((event) => {
    importStringTriggerRef.current = event.currentTarget;
    setManualImportString(getImportString() || '');
    setCopyStatus('');
    setIsImportStringOpen(true);
  }, []);

  const copyManualImportString = useCallback(async () => {
    try {
      await copyImportString();
      setCopyStatus('Importsträngen är kopierad');
    } catch {
      setCopyStatus('Kunde inte kopiera importsträngen');
    }
  }, []);

  const closeTechnicalDetails = useCallback(() => {
    setIsTechnicalOpen(false);
    window.requestAnimationFrame(() => technicalTriggerRef.current?.focus());
  }, []);

  const closeImportString = useCallback(() => {
    setIsImportStringOpen(false);
    window.requestAnimationFrame(() => importStringTriggerRef.current?.focus());
  }, []);

  return (
    <Page>
      <Helmet>
        <title>Aktieutdelningar har flyttat</title>
        <meta
          name="description"
          content="Din lokalt sparade portfölj finns kvar. Jämför våra två kostnadsfria tjänster och flytta datan med ett klick."
        />
      </Helmet>

      <header tw="border-b border-zinc-200 bg-white">
        <div tw="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-7">
          <div tw="flex items-center gap-3">
            <img src={Logo} alt="" tw="h-8 w-8" />
            <span tw="text-sm font-semibold sm:text-base">Aktieutdelningar</span>
          </div>
          <p tw="max-w-[10rem] text-right text-xs font-medium leading-5 text-zinc-500 sm:max-w-none sm:text-sm">
            Den gamla sidan har flyttat!
          </p>
        </div>
      </header>

      <main>
        <div tw="mx-auto max-w-5xl px-4 pb-12 pt-9 sm:px-7 sm:pb-16 sm:pt-12">
          <section tw="grid gap-7 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.75fr)] lg:items-end lg:gap-10">
            <div>
              <h1 tw="max-w-3xl text-[clamp(2.5rem,6vw,4.25rem)] font-semibold leading-[0.98] tracking-[-0.035em] text-zinc-950">
                Du behöver flytta din data{' '}
                <span aria-hidden="true" tw="inline-block align-[0.08em] text-[0.62em]">
                  💙
                </span>
              </h1>
              <p tw="mt-5 max-w-3xl text-base leading-7 text-zinc-600 sm:text-lg sm:leading-8">
                <strong tw="font-semibold text-zinc-950">Din data är inte förlorad.</strong> Din
                portfölj är fortfarande sparad lokalt i den här webbläsaren. För att fortsätta med
                aktuella uppgifter behöver du nu flytta den till en av tjänsterna nedan.
              </p>
              <p tw="mt-3 max-w-3xl text-sm leading-6 text-zinc-600 sm:text-base sm:leading-7">
                Båda tjänsterna drivs vidare av oss och hålls uppdaterade. All ny funktionalitet
                hamnar i huvudappen, medan legacy-appen fortsätter att fungera som den alltid har
                gjort. Du kan flytta till en av dem eller till båda – det är helt upp till dig.
              </p>
            </div>

            <aside tw="border-t border-zinc-200 pt-5 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
              <h2 tw="text-base font-semibold text-zinc-950">Varför behöver jag flytta?</h2>
              <p tw="mt-2 text-sm leading-6 text-zinc-600">
                Den gamla adressen går inte längre att hålla uppdaterad. Därför får den inte nya
                kurser, utdelningar, rapporter eller annan aktiedata.
              </p>
              <button
                ref={technicalTriggerRef}
                type="button"
                onClick={() => setIsTechnicalOpen(true)}
                tw="mt-1.5 inline-flex min-h-10 items-center gap-1.5 rounded-md pr-2 text-sm font-semibold text-indigo-600 underline decoration-indigo-200 underline-offset-4 transition hover:text-indigo-700 hover:decoration-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                Läs mer <ArrowRight size={15} aria-hidden="true" />
              </button>
            </aside>
          </section>

          <div tw="mt-7 sm:mt-9">
            <PortfolioData />
          </div>

          <section tw="mt-11 sm:mt-14" aria-labelledby="destination-heading">
            <div tw="mx-auto mb-10 max-w-2xl text-center">
              <h2
                id="destination-heading"
                tw="text-2xl font-semibold tracking-[-0.025em] text-zinc-950 sm:text-3xl"
              >
                Välj hur du vill fortsätta
              </h2>
              <p tw="mt-2 text-sm leading-6 text-zinc-600 sm:text-base">
                Båda alternativen är gratis. Vårt löfte är att minst den funktionalitet som finns i
                dag ska förbli gratis i båda apparna. Jämför funktionerna och flytta portföljen till
                det som passar dig bäst.
              </p>
            </div>

            <Comparison
              hasPortfolio={hasPortfolio}
              migrateLegacy={migrateLegacy}
              migrateMain={migrateMain}
              migratingTarget={migratingTarget}
              onOpenImportString={openImportString}
            />

            {error && (
              <p
                role="alert"
                tw="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-900"
              >
                {error} Din data ligger fortfarande kvar här på enheten.
              </p>
            )}
          </section>

          <footer tw="mt-10 border-t border-zinc-200 pt-5 text-xs leading-5 text-zinc-500 sm:text-sm">
            <p tw="flex items-center gap-2 font-medium text-zinc-700">
              <ShieldCheck size={17} aria-hidden="true" tw="text-emerald-600" />
              Inget raderas från den här webbläsaren eller den här sidan när du fortsätter.
            </p>
          </footer>
        </div>
      </main>
      {isTechnicalOpen && <TechnicalDetails onClose={closeTechnicalDetails} />}
      {isImportStringOpen && (
        <ImportStringDialog
          copyStatus={copyStatus}
          hasPortfolio={hasPortfolio}
          importString={manualImportString}
          isMigrating={Boolean(migratingTarget)}
          onClose={closeImportString}
          onCopy={copyManualImportString}
          onImport={migrateMain}
        />
      )}
    </Page>
  );
}
